import { useReducer, useCallback } from 'react'
import authService, {
  type CheckIdentityResult,
  type CheckIdentityParams,
  type ClaimAccountParams,
  type MagicLinkParams,
  type CodeSent,
  type Channel,
  type IdentifierType,
} from '@/services/authService'
import { useAuth } from '@/hooks/useAuth'
import logger from '@/utils/logger'
import { classifyIdentifier } from '@/utils/identifier'
import type { ServerError } from '@/types'

/**
 * Login flow state machine.
 *
 * Drives the redesigned /login page from a single state value rather than
 * a basket of useState booleans. Each branch the backend's
 * /auth/check-identity endpoint can return becomes a discrete step with
 * its own component: password (existing user), claim (account exists,
 * no password yet), no-email (account exists in CRM but with no email
 * on file), not-found (no record anywhere), magic-link-sent (after the
 * user asks for a magic link). The `identifier` step is always step 1.
 *
 * The reducer owns: which step we're on, what identifier the customer
 * typed, the result we got back from the server, and any flight-related
 * loading / error flags. Components downstream are dumb renderers.
 *
 * The dispatched actions are coarse-grained and named after user
 * intentions (`identityChecked`, `back`, `magicLinkSent`) rather than
 * implementation details, so the reducer reads as a flow rather than a
 * field-level state mutation list.
 */

// FEDEV-528: mobile is a third way in, for the many customers who have a
// phone number on file and no email.
export type LoginMode = 'email' | 'mobile' | 'customer_number'

export interface Identifier {
  /** The raw single-field input (FEDEV-528). mode + the three fields below
   *  are derived from it by classifyIdentifier, so the rest of the flow keeps
   *  its three-field contract unchanged. */
  value: string
  mode: LoginMode
  email: string
  mobile: string
  customerNumber: string
}

/**
 * Why we stopped. Recorded in our logs; the customer is told the same thing
 * whichever it was.
 *
 *   not-found            nobody matches what they typed.
 *   ambiguous-identifier the email or mobile is on more than one customer
 *                        record, so choosing one risks signing them into
 *                        somebody else's account.
 *   no-contact-method    we hold only a landline, so there is nowhere to send
 *                        a code.
 *
 * These share a screen rather than each explaining itself. This screen is
 * reachable by anyone who can type an identifier, without signing in, so a
 * distinct screen per reason is a distinct answer per reason: "registered to
 * more than one account" confirms the number belongs to real customers and
 * hints how many, and "we hold no email or mobile for you" describes what is
 * on the record. Merging them means an identifier that exists and one that
 * does not produce the same words.
 */
export type DeadEndReason = 'not-found' | 'ambiguous-identifier' | 'no-contact-method'

type Step =
  | { kind: 'identifier' }
  | { kind: 'login' }
  /** verificationRequired: BUG-14, the claim must carry a code emailed
   *  to the address already on the customer's record. */
  | { kind: 'register'; channels: Channel[] }
  /** Code confirmed; all that is left is choosing a password. */
  | { kind: 'set-password' }
  /**
   * They typed a landline. Deliberately NOT one of the dead ends below: this
   * one has an obvious fix, and saying "we can't identify you" for a number
   * we can see is a landline sends them to check details that are fine.
   */
  | { kind: 'landline' }
  /** Stage 1 of the add-email flow: collect the email. */
  | { kind: 'add-email' }
  /** Stage 2 of the add-email flow: code sent, collect code + password. */
  | { kind: 'add-email-verify'; pendingEmail: string }
  /**
   * Every dead end we cannot resolve for the customer, deliberately one step
   * with one screen. `reason` is logged, never shown: see DeadEndReason.
   */
  | { kind: 'not-found'; triedIdentifierType: IdentifierType; reason: DeadEndReason }
  | { kind: 'magic-link-sent'; channel: 'email' | 'sms' }
  /**
   * A business (non-residential) customer tried to sign in. The portal is for
   * residential customers only, so we show them a dedicated message rather than
   * a generic error (FEDEV-565).
   */
  | { kind: 'business-blocked' }

export interface FlowState {
  step: Step
  identifier: Identifier
  loading: boolean
  apiError: string
  fieldErrors: Record<string, string[]>
  /** The failure was Dynamics being unreachable, not anything the customer did. */
  isOutage?: boolean
  /**
   * Identifiers the customer has tried this session that came back as
   * `not_found` or `not_migrated`. Lets the not-found step adapt its
   * copy when the customer has already tried both paths.
   * Format: 'email:foo@bar.com' / 'customer:12345'.
   */
  triedAndUnknown: ReadonlySet<string>
  /**
   * The customer number CRM resolved for whatever the customer typed
   * (FEDEV-528). Signing in and asking for a sign-in link use this rather
   * than the original mobile, so the mobile is only resolved once.
   */
  customerNumber?: string
  /** Where a sign-in link can be sent, masked by the server. */
  channels?: Channel[]
}

const INITIAL_IDENTIFIER: Identifier = {
  value: '',
  mode: 'email',
  email: '',
  mobile: '',
  customerNumber: '',
}

const INITIAL_STATE: FlowState = {
  step: { kind: 'identifier' },
  customerNumber: undefined,
  channels: [],
  identifier: INITIAL_IDENTIFIER,
  loading: false,
  apiError: '',
  fieldErrors: {},
  triedAndUnknown: new Set<string>(),
}

export type { IdentifierType }

/** In the order the customer is offered them. */
const IDENTIFIER_TYPES: IdentifierType[] = ['email', 'mobile', 'customer_number']

function identifierKey(identifier: Identifier): string {
  if (identifier.mode === 'email') return `email:${identifier.email.trim().toLowerCase()}`
  if (identifier.mode === 'mobile') return `mobile:${identifier.mobile.replace(/\s/g, '')}`
  return `customer:${identifier.customerNumber.trim()}`
}

type Action =
  | { type: 'identifierEntered'; value: string }
  | { type: 'submitting' }
  | { type: 'submitFailed'; error: ApiErrorShape }
  | { type: 'identityChecked'; identifier: Identifier; result: CheckIdentityResult }
  | { type: 'codeVerified' }
  | { type: 'magicLinkSent'; channel: 'email' | 'sms' }
  | { type: 'addEmailCodeSent'; pendingEmail: string }
  | { type: 'back' }
  | { type: 'reset' }

interface ApiErrorShape {
  message: string
  fieldErrors: Record<string, string[]>
  /** Dynamics was unreachable; the customer is not at fault (FEDEV-528). */
  isOutage?: boolean
  /** A business account was turned away; show the dedicated screen (FEDEV-565). */
  businessBlocked?: boolean
}

function reducer(state: FlowState, action: Action): FlowState {
  switch (action.type) {
    case 'identifierEntered':
      // One field in, classified into mode + the named fields the rest of the
      // flow already reads, so nothing downstream had to change.
      return {
        ...state,
        identifier: {
          value: action.value,
          ...classifyIdentifier(action.value),
        },
        apiError: '',
        fieldErrors: {},
      }

    case 'submitting':
      return { ...state, loading: true, apiError: '', fieldErrors: {} }

    case 'submitFailed':
      // A business account takes over the whole step with its own screen,
      // rather than showing an inline error under the form (FEDEV-565).
      if (action.error.businessBlocked) {
        return {
          ...state,
          loading: false,
          apiError: '',
          isOutage: false,
          fieldErrors: {},
          step: { kind: 'business-blocked' },
        }
      }
      return {
        ...state,
        loading: false,
        apiError: action.error.message,
        isOutage: action.error.isOutage ?? false,
        fieldErrors: action.error.fieldErrors,
      }

    case 'identityChecked': {
      const { identifier, result } = action
      const base = {
        ...state,
        loading: false,
        apiError: '',
        fieldErrors: {},
        identifier,
      }

      switch (result.mode) {
        case 'login':
          return {
            ...base,
            // Kept so signing in and asking for a link can use the customer
            // number CRM resolved, rather than re-resolving a mobile.
            customerNumber: result.customer_number,
            channels: result.channels ?? [],
            step: { kind: 'login' },
          }
        case 'register':
          return {
            ...base,
            customerNumber: result.customer_number,
            step: {
              kind: 'register',
              channels: result.channels ?? [],
            },
          }
        case 'landline':
          // Not recorded in triedAndUnknown: they have not spent their mobile
          // as a way in, they typed the wrong number. Counting it would make
          // the next screen tell them not to bother trying their real mobile.
          return { ...base, step: { kind: 'landline' } }
        case 'add_email':
          return { ...base, step: { kind: 'add-email' } }
        case 'not_found':
        case 'ambiguous_identifier':
        case 'no_contact_method': {
          // All three are dead ends for the same identifier, so all three
          // count as having tried it: whichever one they hit, that way in is
          // spent and the screen should be offering them the others.
          const next = new Set(state.triedAndUnknown)
          next.add(identifierKey(identifier))

          const reason: DeadEndReason = result.mode === 'ambiguous_identifier'
            ? 'ambiguous-identifier'
            : result.mode === 'no_contact_method'
              ? 'no-contact-method'
              : 'not-found'

          return {
            ...base,
            triedAndUnknown: next,
            step: {
              kind: 'not-found',
              // Older dead-end responses carried no tried type. Falling back
              // to what was classified locally keeps the copy naming the
              // right thing rather than rendering a blank.
              triedIdentifierType: result.tried_identifier_type ?? identifier.mode,
              reason,
            },
          }
        }
      }
      // Exhaustive — every mode above returns. Re-stating `return base`
      // here so the linter doesn't see an implicit fall-through into
      // the next case if a future mode is added without a matching
      // branch above.
      return base
    }

    case 'codeVerified':
      // Code accepted; all that is left is choosing a password (FEDEV-528).
      return { ...state, loading: false, apiError: '', fieldErrors: {}, step: { kind: 'set-password' } }

    case 'magicLinkSent':
      return {
        ...state,
        loading: false,
        apiError: '',
        fieldErrors: {},
        step: { kind: 'magic-link-sent', channel: action.channel },
      }

    case 'addEmailCodeSent':
      return {
        ...state,
        loading: false,
        apiError: '',
        fieldErrors: {},
        step: { kind: 'add-email-verify', pendingEmail: action.pendingEmail },
      }

    case 'back':
      // Wipes the secondary-step state but preserves whatever the user
      // typed in the identifier so they can correct rather than retype.
      return {
        ...state,
        loading: false,
        apiError: '',
        fieldErrors: {},
        step: { kind: 'identifier' },
      }

    case 'reset':
      return INITIAL_STATE
  }
}

/**
 * Standard mapping of an axios-shape server error back to a friendly
 * message + field errors. Lifted here so every action handler in the
 * flow uses the same shape.
 */
/**
 * Outcome of asking for a verification code. Carries the reason on failure so
 * the step can tell the customer why no code is coming, rather than leaving
 * them at a code box that will never be satisfied.
 */
export interface CodeSendResult {
  sent: CodeSent | null
  error: string | null
}

function asApiError(error: unknown, fallback: string): ApiErrorShape {
  const e = error as ServerError & { isOutage?: boolean }
  logger.error('Auth error:', e)

  // FEDEV-528: Dynamics is unreachable. Kept apart from ordinary errors so
  // the customer is told to come back shortly rather than shown something
  // that reads as though they did wrong.
  if (e?.isOutage || (e?.body as Record<string, unknown>)?.outage === true) {
    return {
      message: ((e?.body as Record<string, unknown>)?.message as string) || fallback,
      fieldErrors: {},
      isOutage: true,
    }
  }

  // FEDEV-565: a business (non-residential) customer. The backend flags it with
  // a stable `code` so we can show the specific business-account screen instead
  // of a generic error, whatever the message text happens to be.
  if ((e?.body as Record<string, unknown>)?.code === 'business_account_blocked') {
    return {
      message: ((e?.body as Record<string, unknown>)?.message as string) || fallback,
      fieldErrors: {},
      businessBlocked: true,
    }
  }
  if (e?.body) {
    const next: Record<string, string[]> = {}
    if (e.body.errors && typeof e.body.errors === 'object') {
      Object.entries(e.body.errors).forEach(([field, messages]) => {
        next[field] = Array.isArray(messages) ? messages : [messages as string]
      })
    }
    return { message: (e.body.message as string) || fallback, fieldErrors: next }
  }
  return { message: e?.message || fallback, fieldErrors: {} }
}

function pickIdentifierForApi(
  identifier: Identifier,
): CheckIdentityParams {
  if (identifier.mode === 'email') return { email: identifier.email.trim() }
  // Sent as typed; the server normalises it (FEDEV-528).
  if (identifier.mode === 'mobile') return { mobile: identifier.mobile.trim() }
  return { customer_number: identifier.customerNumber.trim() }
}

/**
 * What to send once a mobile has been resolved.
 *
 * Signing in and asking for a link both need an identifier the server can act
 * on, and a mobile is not one: it was resolved to a customer number at the
 * identity step, so that is what goes back. Avoids a second CRM lookup on
 * every password attempt.
 */
function resolvedIdentifierForApi(
  identifier: Identifier,
  customerNumber?: string,
): CheckIdentityParams {
  if (customerNumber) return { customer_number: customerNumber }
  return pickIdentifierForApi(identifier)
}

export function useLoginFlow() {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE)
  const { login: contextLogin, setSession } = useAuth()

  const checkIdentity = useCallback(async () => {
    dispatch({ type: 'submitting' })
    try {
      const result = await authService.checkIdentity(
        pickIdentifierForApi(state.identifier),
      )
      logger.info('Identity check result', { mode: result.mode })
      dispatch({ type: 'identityChecked', identifier: state.identifier, result })
    } catch (error) {
      dispatch({
        type: 'submitFailed',
        error: asApiError(error, 'Something went wrong. Please try again.'),
      })
    }
  }, [state.identifier])

  const signIn = useCallback(
    async (password: string, rememberMe: boolean) => {
      dispatch({ type: 'submitting' })
      try {
        await contextLogin({
          ...pickIdentifierForApi(state.identifier),
          password,
          rememberMe,
        })
        return true
      } catch (error) {
        dispatch({
          type: 'submitFailed',
          error: asApiError(error, 'Invalid credentials. Please try again.'),
        })
        return false
      }
    },
    [contextLogin, state.identifier],
  )

  const claimAccount = useCallback(
    async (password: string, passwordConfirmation: string, verificationCode?: string) => {
      dispatch({ type: 'submitting' })
      try {
        const params: ClaimAccountParams = {
          ...pickIdentifierForApi(state.identifier),
          password,
          password_confirmation: passwordConfirmation,
          verification_code: verificationCode,
        }
        const result = await authService.claimAccount(params)
        setSession(result)
        return true
      } catch (error) {
        dispatch({
          type: 'submitFailed',
          error: asApiError(error, "Couldn't set up your account. Please try again."),
        })
        return false
      }
    },
    [setSession, state.identifier],
  )

  /**
   * BUG-14: ask the backend to email a claim verification code to the
   * address on the customer's record. Returns the masked address on success,
   * or null on failure. Deliberately does NOT set the blocking flow error:
   * the code step auto-requests on mount, and a failure there (e.g. a
   * throttled resend when a valid code was already sent) must not block the
   * customer, they can still type the code they have or use the resend
   * button, which surfaces its own inline feedback.
   */
  const requestClaimCode = useCallback(async (channel?: 'email' | 'sms'): Promise<CodeSendResult> => {
    try {
      const sent = await authService.requestClaimCode({
        ...pickIdentifierForApi(state.identifier),
        channel,
      })

      return { sent, error: null }
    } catch (error) {
      // The reason is worth keeping. Throttling ("Too many messages
      // requested...") and the two-send cap both land here, and swallowing
      // them left the customer staring at a code box with no code coming and
      // nothing on screen to explain why.
      return {
        sent: null,
        error: asApiError(error, 'We could not send a code just now. Please try again shortly.').message,
      }
    }
  }, [state.identifier])

  /**
   * Confirm the code on its own, before any password is chosen (FEDEV-528).
   */
  const verifyClaimCode = useCallback(async (code: string): Promise<boolean> => {
    dispatch({ type: 'submitting' })
    try {
      await authService.verifyClaimCode({ ...pickIdentifierForApi(state.identifier), code })
      dispatch({ type: 'codeVerified' })
      return true
    } catch (error) {
      dispatch({
        type: 'submitFailed',
        error: asApiError(error, 'That code was not right. Please check it and try again.'),
      })
      return false
    }
  }, [state.identifier])

  /**
   * Stage 1 of the add-email flow. Customer found in CRM by customer
   * number but the contact has no email; backend issues a 6-digit
   * code to the address they typed and we transition to the verify
   * step where they'll enter the code + chosen password.
   */
  const requestAddEmailCode = useCallback(
    async (email: string) => {
      const customerNumber = state.identifier.customerNumber.trim()
      const trimmedEmail = email.trim().toLowerCase()
      dispatch({ type: 'submitting' })
      try {
        await authService.requestAddEmailCode({
          customer_number: customerNumber,
          email: trimmedEmail,
        })
        dispatch({ type: 'addEmailCodeSent', pendingEmail: trimmedEmail })
        return true
      } catch (error) {
        dispatch({
          type: 'submitFailed',
          error: asApiError(error, "Couldn't send a verification code. Please try again."),
        })
        return false
      }
    },
    [state.identifier],
  )

  /**
   * Stage 2: customer types the code + password. On success the
   * backend PATCHes Dynamics with the email, creates the portal
   * user, and returns an auth token; we drop them straight into the
   * dashboard via setSession.
   */
  const verifyAddEmail = useCallback(
    async (code: string, password: string, passwordConfirmation: string) => {
      const customerNumber = state.identifier.customerNumber.trim()
      // Pull pending email off the current step so the customer can't
      // tamper with the form-submitted email mid-flow.
      const pendingEmail =
        state.step.kind === 'add-email-verify' ? state.step.pendingEmail : ''
      dispatch({ type: 'submitting' })
      try {
        const result = await authService.verifyAddEmail({
          customer_number: customerNumber,
          email: pendingEmail,
          code: code.trim(),
          password,
          password_confirmation: passwordConfirmation,
        })
        setSession(result)
        return true
      } catch (error) {
        dispatch({
          type: 'submitFailed',
          error: asApiError(error, "Couldn't verify your code. Please try again."),
        })
        return false
      }
    },
    [setSession, state.identifier, state.step],
  )

  const requestMagicLink = useCallback(async (channel?: 'email' | 'sms') => {
    dispatch({ type: 'submitting' })
    try {
      const params: MagicLinkParams = resolvedIdentifierForApi(
        state.identifier,
        state.customerNumber,
      )
      await authService.requestMagicLink({ ...params, channel })

      // Report the channel the link actually went by, not how the customer
      // identified themselves: someone can identify by email and then ask for
      // a text. When no channel was picked the server prefers email where it
      // has one, so mirror that rather than guessing.
      const used: 'email' | 'sms' = channel
        ?? (state.channels?.some((c) => c.type === 'email') ? 'email' : 'sms')

      dispatch({ type: 'magicLinkSent', channel: used })
    } catch (error) {
      dispatch({
        type: 'submitFailed',
        error: asApiError(error, "Couldn't send a login link. Please try again."),
      })
    }
  }, [state.identifier, state.customerNumber, state.channels])

  /**
   * Which of the three identifiers the customer has already tried and had
   * turned away this session, and which are left.
   *
   * This used to check email and customer number only, so a customer who had
   * tried their mobile was still told to try it. FEDEV-528 added mobile as a
   * third way in and this had not caught up.
   */
  const triedTypes = new Set<IdentifierType>(
    Array.from(state.triedAndUnknown).map((k) =>
      k.startsWith('email:') ? 'email' : k.startsWith('mobile:') ? 'mobile' : 'customer_number'
    )
  )

  const untriedIdentifierTypes = IDENTIFIER_TYPES.filter((t) => !triedTypes.has(t))
  const hasTriedAllIdentifierTypes = untriedIdentifierTypes.length === 0

  return {
    state,
    derived: {
      untriedIdentifierTypes,
      hasTriedAllIdentifierTypes,
    },
    actions: {
      setIdentifier: (value: string) => dispatch({ type: 'identifierEntered', value }),
      back: () => dispatch({ type: 'back' }),
      reset: () => dispatch({ type: 'reset' }),
      checkIdentity,
      signIn,
      claimAccount,
      requestClaimCode,
    verifyClaimCode,
      requestMagicLink,
      requestAddEmailCode,
      verifyAddEmail,
    },
  }
}
